import type { RefObject } from 'react';
import { useCallback, useRef } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import type { SingleFormProps } from 'src/app/shared/components/payments';

import { usePromoteFormData, usePromoteFormState } from '../../../../../formContext';
import { useSharedHooks } from '../../../hooks/useSharedHooks';

type CardFormData = Pick<SingleFormProps, 'name' | 'email' | 'paymentMethodSaveOptions'>;

type CardFormActions = Pick<
	SingleFormProps,
	| 'beforeToken'
	| 'onTokenError'
	| 'onTokenInvalid'
	| 'validate'
	| 'withToken'
	| 'prePaymentChecks'
	| 'onCardDetailsFieldFocus'
>;

type Result = ModelHookResult<
	CardFormData & {
		loading: boolean;
		disabled: boolean;
		ref: RefObject<HTMLSpanElement | null>;
		countryCode: string;
		currency: string;
	},
	CardFormActions
>;

export function useStripeCreditCard(): Result {
	const [{ name, paymentMethodSaveOptions, formState }] = usePromoteFormState();

	const {
		currency: { code },
		user: { email },
		countryCode,
	} = usePromoteFormData();

	const track = useTracking();
	const ref = useRef<HTMLSpanElement>(null);
	const { inProgress, ...rest } = useSharedHooks();

	const trackCardFieldFocus = useCallback(
		(field: 'cvc' | 'expiry' | 'number' | 'card') => {
			void track(`pp_focus_card_${field}`);
		},
		[track],
	);

	return {
		data: {
			ref,
			email,
			name,
			paymentMethodSaveOptions,
			loading: inProgress,
			disabled: inProgress || formState.status === 'loading',
			countryCode,
			currency: code,
		},
		actions: {
			...rest,
			onCardDetailsFieldFocus: () => trackCardFieldFocus('card'),
		},
	};
}
